import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { Phone, Mail, MapPin, Send, Clock } from "lucide-react";
import { company } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 15000 });
      toast({
        title: "Message sent!",
        description: "Thank you. Eddie will get back to you within 24 hours.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Please try again or call us directly.";
      toast({
        title: "Couldn't send message",
        description: typeof detail === "string" ? detail : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-14 sm:py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 grid lg:grid-cols-5 gap-7 lg:gap-10">
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-cyan-700 font-bold">Contact Us</div>
          <h2 className="mt-2 text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
            Let&rsquo;s elevate your digital game.
          </h2>
          <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
            Need help with websites, social media, or Google? With 20+ years of experience, two offices
            (Nashville, TN &amp; Phoenix, AZ) and a USMC veteran at the helm, we&rsquo;ll help you outsmart
            your competition.
          </p>

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-1 gap-3 lg:gap-4">
            <a
              href={`tel:${company.phone}`}
              className="flex items-start gap-3 lg:gap-4 p-3.5 lg:p-4 rounded-xl bg-white border border-slate-200 hover:border-cyan-500 hover:shadow-md"
            >
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-cyan-600 text-white grid place-items-center flex-shrink-0">
                <Phone className="h-4 w-4 lg:h-5 lg:w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] lg:text-xs uppercase tracking-wider text-slate-500 font-bold">Call</div>
                <div className="font-bold text-slate-900 text-sm sm:text-base truncate">{company.phone}</div>
              </div>
            </a>

            <a
              href={`mailto:${company.email}`}
              className="flex items-start gap-3 lg:gap-4 p-3.5 lg:p-4 rounded-xl bg-white border border-slate-200 hover:border-cyan-500 hover:shadow-md"
            >
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-cyan-600 text-white grid place-items-center flex-shrink-0">
                <Mail className="h-4 w-4 lg:h-5 lg:w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] lg:text-xs uppercase tracking-wider text-slate-500 font-bold">Email</div>
                <div className="font-bold text-slate-900 text-xs sm:text-sm lg:text-base break-all">{company.email}</div>
              </div>
            </a>

            <div className="flex items-start gap-3 lg:gap-4 p-3.5 lg:p-4 rounded-xl bg-white border border-slate-200">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-cyan-600 text-white grid place-items-center flex-shrink-0">
                <MapPin className="h-4 w-4 lg:h-5 lg:w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] lg:text-xs uppercase tracking-wider text-slate-500 font-bold">Locations</div>
                <div className="font-bold text-slate-900 text-sm lg:text-base">Nashville, TN &middot; Phoenix, AZ</div>
                <div className="text-[11px] lg:text-xs text-slate-500 mt-0.5">Serving the entire USA via Zoom &amp; FaceTime</div>
              </div>
            </div>

            <div className="flex items-start gap-3 lg:gap-4 p-3.5 lg:p-4 rounded-xl bg-white border border-slate-200">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-cyan-600 text-white grid place-items-center flex-shrink-0">
                <Clock className="h-4 w-4 lg:h-5 lg:w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] lg:text-xs uppercase tracking-wider text-slate-500 font-bold">Response Time</div>
                <div className="font-bold text-slate-900 text-sm lg:text-base">Within 24 hours</div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="lg:col-span-3 rounded-2xl bg-white border border-slate-200 p-5 sm:p-7 md:p-9 shadow-sm"
        >
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">Reach out</h3>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Tell us about your business and how we can help.</p>

          <div className="mt-5 lg:mt-6 grid sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1.5 lg:space-y-2">
              <Label htmlFor="name" className="text-sm">Name *</Label>
              <Input
                id="name"
                name="name"
                required
                value={form.name}
                onChange={handleChange}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-1.5 lg:space-y-2">
              <Label htmlFor="email" className="text-sm">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div className="mt-3.5 lg:mt-4 space-y-1.5 lg:space-y-2">
            <Label htmlFor="phone" className="text-sm">Phone *</Label>
            <Input
              id="phone"
              name="phone"
              required
              value={form.phone}
              onChange={handleChange}
              placeholder="(615) 555-1234"
            />
          </div>

          <div className="mt-3.5 lg:mt-4 space-y-1.5 lg:space-y-2">
            <Label htmlFor="message" className="text-sm">Message / Comments *</Label>
            <Textarea
              id="message"
              name="message"
              required
              rows={4}
              value={form.message}
              onChange={handleChange}
              placeholder="Tell us about your business goals..."
              className="lg:min-h-[140px]"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="mt-5 lg:mt-6 w-full bg-slate-900 hover:bg-slate-800 text-white h-11 sm:h-12 font-semibold text-sm sm:text-base"
          >
            {submitting ? "Sending..." : (
              <>
                Let&rsquo;s Talk <Send className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
          <p className="mt-3 text-[11px] sm:text-xs text-slate-500 text-center">
            By submitting, you agree to be contacted about your inquiry.
          </p>
        </form>
      </div>
    </section>
  );
}
